import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Section, Container, Badge } from '../components/UIComponents';
import { Button } from '../components/Button';
import { Search, ChevronRight, ShoppingCart } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import API_BASE_URL from '../config/api';
import { getEffectivePriceWithOffer } from '../utils/pricing';

const HeroSection = () => {
  return (
    <section className="bg-gradient-to-br from-slate-50 via-teal-50 to-blue-50 pt-8 md:pt-16 pb-16 md:pb-28">
      <Container>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center max-w-3xl mx-auto"
        >
          <Badge variant="primary">Premium Medical Equipment Catalog</Badge>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 mb-6 leading-tight mt-6">
            Our <span className="text-teal-600">Complete Product Range</span>
          </h1>
          <p className="text-lg md:text-xl text-slate-600 leading-relaxed">
            Browse our complete selection of surgical equipment and medical supports trusted by healthcare professionals.
          </p>
        </motion.div>
      </Container>
    </section>
  );
};

const ProductGridSection = ({ products, categories, onCategoryChange, selectedCategory, onSearch, searchTerm, token, isLoading }) => {
  const [cartLoading, setCartLoading] = useState({});
  const [sizeModalOpen, setSizeModalOpen] = useState(null);
  const [selectedSizes, setSelectedSizes] = useState({});
  const [selectedSide, setSelectedSide] = useState({}); // { [productId]: 'right' | 'left' | null }

  const getProductSelections = (productId) => selectedSizes[productId] || {};
  const updateProductSelections = (productId, key, value) => {
    setSelectedSizes(prev => ({
      ...prev,
      [productId]: {
        ...(prev[productId] || {}),
        [key]: value,
      },
    }));
  };

  const handleCategoryClick = (categoryName) => {
    onCategoryChange(categoryName === selectedCategory ? 'all' : categoryName);
  };

  const addToCart = async (product) => {
    if (!token) {
      alert('Please login to add items to cart!');
      return;
    }

    const productSelections = getProductSelections(product.id);

    // Get total quantity selected
    const totalQty = Object.values(productSelections).reduce((sum, qty) => sum + (qty || 0), 0);
    
    if (totalQty === 0) {
      alert(product.hasNoSizeSelection ? 'Please select a quantity!' : 'Please select at least one size and quantity!');
      return;
    }

    // If product hasRightLeft, validate side is selected
    const productSide = selectedSide[product.id];
    if (product.hasRightLeft && !productSide) {
      alert('Please select Right or Left side for this product!');
      return;
    }

    setCartLoading({ ...cartLoading, [product.id]: true });

    try {
      // Add each size separately
      const sizeEntries = Object.entries(productSelections).filter(([_, qty]) => qty > 0);
      
      for (const [sizeId, quantity] of sizeEntries) {
        const response = await fetch(`${API_BASE_URL}/api/cart/add`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({
            productId: product.id,
                                    sizeId: sizeId === 'no-size' ? null : parseInt(sizeId),
                                    quantity: parseInt(quantity),
                                    side: product.hasRightLeft ? (productSide || null) : null,
                                  }),
        });

        if (!response.ok) {
          const data = await response.json();
          throw new Error(data.message || 'Failed to add to cart');
        }
      }

      alert('✅ Added to cart!');
      setSizeModalOpen(null);
      setSelectedSizes(prev => ({ ...prev, [product.id]: {} }));
      // Dispatch event to update cart count in navbar instantly
      window.dispatchEvent(new Event('cartUpdated'));
    } catch (error) {
      console.error('Error adding to cart:', error);
      alert('❌ Error: ' + error.message);
    } finally {
      setCartLoading({ ...cartLoading, [product.id]: false });
    }
  };

  // Filter products based on category and search
  const filteredProducts = products.filter(product => {
    const matchesCategory = selectedCategory === 'all' || product.category === selectedCategory;
    const matchesSearch = !searchTerm || 
      product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (product.description && product.description.toLowerCase().includes(searchTerm.toLowerCase()));
    return matchesCategory && matchesSearch;
  });

  // Skeleton loader for product grid
  const SkeletonCard = () => (
    <div className="bg-white rounded-lg overflow-hidden shadow-md animate-pulse">
      <div className="bg-slate-200 h-48 md:h-40"></div>
      <div className="p-4">
        <div className="h-3 bg-slate-200 rounded mb-2 w-20"></div>
        <div className="h-4 bg-slate-200 rounded mb-3"></div>
        <div className="h-3 bg-slate-200 rounded mb-4 w-32"></div>
        <div className="h-10 bg-slate-200 rounded"></div>
      </div>
    </div>
  );

  return (
    <Section className="bg-white">
      <Container>
        {/* Search and Filter Bar */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-12"
        >
          {/* Search Input */}
          <div className="mb-8">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-slate-400" size={20} />
              <input
                type="text"
                placeholder="Search products by name or description..."
                value={searchTerm}
                onChange={(e) => onSearch(e.target.value)}
                className="w-full pl-12 pr-4 py-3 border border-slate-300 rounded-lg focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-200 transition-colors"
              />
            </div>
          </div>

          {/* Category Filter Tags */}
          <div className="flex flex-wrap gap-3 items-center">
            <span className="text-sm font-semibold text-slate-600">Categories:</span>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => onCategoryChange('all')}
              className={`px-4 py-2 rounded-full font-medium transition-all ${
                selectedCategory === 'all'
                  ? 'bg-teal-600 text-white shadow-md'
                  : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
              }`}
            >
              All
            </motion.button>

            {categories.map((category, i) => (
              <motion.button
                key={category.id || i}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => handleCategoryClick(category.name)}
                className={`px-4 py-2 rounded-full font-medium transition-all ${
                  selectedCategory === category.name
                    ? 'bg-teal-600 text-white shadow-md'
                    : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                }`}
              >
                {category.name}
              </motion.button>
            ))}
          </div>

          {/* Results Count */}
          {!isLoading && (
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-sm text-slate-600 mt-4 font-medium"
            >
              Showing <span className="font-bold text-slate-900">{filteredProducts.length}</span> product(s)
            </motion.p>
          )}
        </motion.div>

        {/* Products Grid or Skeleton */}
        {isLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <SkeletonCard key={i} />
            ))}
          </div>
        ) : filteredProducts.length > 0 ? (
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
          >
            {filteredProducts.map((product, i) => (
              <motion.div
                key={product.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.05 }}
                className="bg-white rounded-lg overflow-hidden shadow-md hover:shadow-lg transition-shadow flex flex-col"
              >
                {/* Product Image */}
                <div className="bg-gradient-to-br from-slate-100 to-slate-200 h-48 md:h-40 flex items-center justify-center relative group overflow-hidden">
                  {product.image ? (
                    <img 
                      src={product.image} 
                      alt={product.name}
                      className="w-full h-full object-contain group-hover:scale-110 transition-transform duration-300"
                    />
                  ) : (
                    <div className="text-6xl opacity-80 group-hover:opacity-100 group-hover:scale-110 transition-all duration-300">📦</div>
                  )}
                  {product.discount > 0 && (
                    <div className="absolute top-3 right-3 bg-red-500 text-white px-3 py-1 rounded-full text-sm font-bold">
                      -{product.discount}%
                    </div>
                  )}
                </div>

                {/* Product Info */}
                <div className="p-4 flex flex-col flex-grow">
                  <p className="text-xs uppercase text-slate-500 font-semibold mb-1">{product.category || 'Medical Equipment'}</p>
                  <h3 className="text-sm font-bold text-slate-900 mb-2 line-clamp-2">{product.name}</h3>
                  <p className="text-xs text-slate-600 mb-3 line-clamp-2 flex-grow">{product.description || 'Premium medical equipment'}</p>
                  
                  {/* Price */}
                  <div className="flex items-baseline gap-2 mb-3">
                    {(() => {
                      // Check if this is a per-size pricing product
                      const categoryData = categories.find(c => c.name?.toLowerCase() === product.category?.toLowerCase());
                      const productUsesPerSizePricing = product.usesPerSizePricing === true;
                      const isPerSizePricing = categoryData?.usesPerSizePricing === true || product.category?.toLowerCase() === 'tapes' || productUsesPerSizePricing;
                      if (isPerSizePricing && product.sizes && product.sizes.length > 0) {
                        // Calculate price range from sizes
                        const prices = product.sizes
                          .filter(s => s.price && s.price > 0)
                          .map(s => {
                            const discount = s.discount ?? product.discount ?? 0;
                            const discountPercent = discount > 1 ? discount / 100 : discount;
                            return Math.round(s.price - (s.price * discountPercent));
                          });
                        
                        if (prices.length > 0) {
                          const minPrice = Math.min(...prices);
                          const maxPrice = Math.max(...prices);
                          return (
                            <div className="flex flex-col gap-1">
                              <span className="text-lg font-bold text-teal-600">
                                {minPrice === maxPrice 
                                  ? `₹${minPrice.toLocaleString('en-IN')}`
                                  : `₹${minPrice.toLocaleString('en-IN')} - ₹${maxPrice.toLocaleString('en-IN')}`
                                }
                              </span>
                              <span className="text-xs text-slate-500 font-semibold">Select Size to View Price</span>
                            </div>
                          );
                        }
                      }
                      
                      // Regular pricing
                      if (product.discount > 0) {
                        return (
                          <>
                            <span className="text-lg font-bold text-teal-600">₹{Math.round(product.price - (product.price * product.discount / 100)).toLocaleString('en-IN')}</span>
                            <span className="text-sm text-slate-500 line-through">
                              ₹{product.price.toLocaleString('en-IN')}
                            </span>
                            <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded font-semibold">
                              Save {product.discount}%
                            </span>
                          </>
                        );
                      } else {
                        return <span className="text-lg font-bold text-teal-600">₹{product.price.toLocaleString('en-IN')}</span>;
                      }
                    })()}
                  </div>

                  {/* Bulk Offers - show discount only, with size info */}
                  {product.offers && product.offers.length > 0 && (
                    <div className="mb-2 space-y-1">
                      {product.offers.map((offer, idx) => {
                        const sizeName = offer.sizeId && offer.sizeId !== 'all'
                          ? (product.sizes || []).find(s => String(s.sizeId || s.id) === String(offer.sizeId))?.name
                          : null;
                        return (
                          <div key={idx} className="text-xs font-semibold text-green-700 bg-green-50 border border-green-200 px-2 py-1 rounded inline-block mr-1">
                            {offer.label || `Buy ${offer.minQty}+ get ${offer.discountPercent || Math.round(((product.price - offer.rate) / product.price) * 100)}% off`}
                            {sizeName && <span className="ml-1 text-green-800">(Size {sizeName})</span>}
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* Stock Status - Generic only, no quantities shown to customer */}
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-green-600 text-sm font-semibold">✓ Available</span>
                  </div>

                  {/* Right/Left requirement indicator */}
                  {product.hasRightLeft && (
                    <div className="mb-3">
                      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-amber-50 border border-amber-200 text-amber-700 text-xs font-bold">
                        🔄 Requires Right/Left Selection
                      </span>
                    </div>
                  )}

                  {/* Add to Cart / Select Size Button */}
                  <motion.button
                    onClick={() => setSizeModalOpen(product.id)}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="w-full bg-teal-600 hover:bg-teal-700 text-white font-semibold py-2 px-4 rounded-lg transition-colors flex items-center justify-center gap-2 mt-auto"
                  >
                    <ShoppingCart size={16} />
                    {product.hasNoSizeSelection ? 'Select Quantity' : 'Select Size/Quantity'}
                  </motion.button>
                </div>
              </motion.div>
            ))}
          </motion.div>
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center py-16"
          >
            <p className="text-xl text-slate-600 font-semibold mb-4">No products found</p>
            <p className="text-slate-500">Try adjusting your search or filters</p>
          </motion.div>
        )}
      </Container>

      {/* Size Selection Modal */}
      {sizeModalOpen && products.find(p => p.id === sizeModalOpen) && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={() => setSizeModalOpen(null)}
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            onClick={(e) => e.stopPropagation()}
            className="bg-white rounded-lg p-6 max-w-md w-full max-h-[70vh] overflow-y-auto flex flex-col"
          >
            {(() => {
              const product = products.find(p => p.id === sizeModalOpen);
              if (!product) return null;

              // Check if category uses per-size pricing
              const categoryData = categories.find(c => c.name?.toLowerCase() === product.category?.toLowerCase());
              const productUsesPerSizePricing = product.usesPerSizePricing === true;
              const isTapesCategory = categoryData?.usesPerSizePricing === true || product.category?.toLowerCase() === 'tapes' || productUsesPerSizePricing;
              
              const currentSelections = getProductSelections(product.id);

              // Calculate total quantity and amount
              const totalQty = Object.values(currentSelections).reduce((sum, qty) => sum + (qty || 0), 0);
              const hasSelection = Object.values(currentSelections).some(qty => qty > 0);
              
              // Calculate total amount based on per-size pricing (for Tapes) or product pricing (for others)
              let totalAmount = 0;
              let anyOfferApplied = null;
              if (isTapesCategory && product.sizes && product.sizes.length > 0) {
                // Per-size pricing for Tapes
                totalAmount = product.sizes.reduce((sum, size) => {
                  const sizeKey = `${size.sizeId || size.id}`;
                  const qty = currentSelections[sizeKey] || 0;
                  if (qty > 0) {
                    const sizePrice = size.price || product.price;
                    const sizeDiscount = size.discount ?? product.discount ?? 0;
                    // Ensure discount is percentage
                    const discountPercent = sizeDiscount > 1 ? sizeDiscount / 100 : sizeDiscount;
                    // Selling price from MRP only, not from cost
                    const sellingPrice = Math.round(sizePrice - (sizePrice * discountPercent));
                    // Apply size-specific or all-sizes bulk offer for this size line
                    const { price: offerPrice, offerApplied } = getEffectivePriceWithOffer(sellingPrice, qty, product.offers || [], size.sizeId || size.id);
                    if (offerApplied && (!anyOfferApplied || offerApplied.effectiveDiscount > anyOfferApplied.effectiveDiscount)) {
                      anyOfferApplied = offerApplied;
                    }
                    return sum + (offerPrice * qty);
                  }
                  return sum;
                }, 0);
              } else {
                // Product-level pricing for other categories
                const sellingPrice = Math.round(product.price - (product.price * (product.discount || 0) / 100));
                const { price: offerPrice, offerApplied } = getEffectivePriceWithOffer(sellingPrice, totalQty, product.offers || []);
                totalAmount = offerPrice * totalQty;
                anyOfferApplied = offerApplied;
              }

              const modalOfferApplied = anyOfferApplied;

              return (
                <div>
                  <div className="flex justify-between items-start mb-4">
                  <div>
                    <h2 className="text-xl font-bold text-slate-900">{product.name}</h2>
                    {product.hasNoSizeSelection && (
                      <p className="text-sm text-teal-600 font-semibold mt-1">📦 Quantity-based product (no size selection)</p>
                    )}
                    {!isTapesCategory ? (
                        <div className="mt-2 flex items-center gap-3">
                          <p className="text-slate-500 line-through text-sm">₹{product.price.toLocaleString('en-IN')}</p>
                          <p className="text-teal-600 font-bold text-lg">₹{Math.round(product.price - (product.price * (product.discount || 0) / 100)).toLocaleString('en-IN')}</p>
                          {product.discount > 0 && <span className="bg-red-100 text-red-700 font-bold px-2 py-1 rounded text-xs">-{product.discount}%</span>}
                        </div>
                      ) : (
                        <p className="text-slate-500 text-sm mt-2">📏 Per-inch pricing below</p>
                      )}
                    </div>
                    <button
                      onClick={() => setSizeModalOpen(null)}
                      className="text-2xl font-bold text-slate-400 hover:text-slate-600"
                    >
                      ✕
                    </button>
                  </div>

                  {/* Right/Left Selection for products that need it */}
                  {product.hasRightLeft && (
                    <div className="mb-6">
                      <p className="text-sm font-semibold text-slate-700 mb-3">🔄 Select Side:</p>
                      <div className="flex gap-4">
                        <button
                          onClick={() => setSelectedSide({ ...selectedSide, [product.id]: 'right' })}
                          className={`flex-1 px-4 py-3 rounded-lg font-bold text-lg transition-all border-2 ${
                            selectedSide[product.id] === 'right'
                              ? 'bg-teal-100 border-teal-600 text-teal-800'
                              : 'bg-slate-50 border-slate-300 text-slate-700 hover:border-teal-400'
                          }`}
                        >
                          ➡️ Right
                        </button>
                        <button
                          onClick={() => setSelectedSide({ ...selectedSide, [product.id]: 'left' })}
                          className={`flex-1 px-4 py-3 rounded-lg font-bold text-lg transition-all border-2 ${
                            selectedSide[product.id] === 'left'
                              ? 'bg-teal-100 border-teal-600 text-teal-800'
                              : 'bg-slate-50 border-slate-300 text-slate-700 hover:border-teal-400'
                          }`}
                        >
                          ⬅️ Left
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Sizes List */}
                  <div className="mb-6">
                    <p className="text-sm font-semibold text-slate-700 mb-3">
                      {product.hasNoSizeSelection ? 'Select Quantity:' : 'Select Size & Quantity:'}
                    </p>
                    {product.hasNoSizeSelection ? (
                      <div>
                        <div className="flex items-center gap-2 mb-3">
                          <button
                            onClick={() => updateProductSelections(product.id, 'no-size', Math.max(0, (currentSelections['no-size'] || 0) - 1))}
                            className="w-8 h-8 bg-slate-200 hover:bg-slate-300 rounded font-bold text-slate-700"
                          >
                            −
                          </button>
                          <input
                            type="number"
                            value={currentSelections['no-size'] || 0}
                            onChange={(e) => updateProductSelections(product.id, 'no-size', Math.max(0, parseInt(e.target.value) || 0))}
                            className="w-16 text-center px-2 py-1 border border-slate-200 rounded font-semibold"
                          />
                          <button
                            onClick={() => updateProductSelections(product.id, 'no-size', (currentSelections['no-size'] || 0) + 1)}
                            className="w-8 h-8 bg-teal-600 hover:bg-teal-700 text-white rounded font-bold"
                          >
                            +
                          </button>
                        </div>
                        <div className="text-sm text-slate-700">
                          <span className="font-semibold">Total Amount: </span>
                          <span className="font-bold text-teal-600 text-lg">₹{totalAmount.toLocaleString('en-IN')}</span>
                        </div>
                      </div>
                    ) : product.sizes && product.sizes.length > 0 ? (
                      <div className="space-y-3">
                        {product.sizes.map((size) => {
                          const sizeKey = `${size.sizeId || size.id}`;
                          const qty = currentSelections[sizeKey] || 0;
                          
                          // For Tapes: use per-size pricing; otherwise use product pricing
                          const displayPrice = isTapesCategory ? (size.price || product.price) : product.price;
                          const displayPurchasePrice = isTapesCategory ? (size.purchasePrice || 0) : 0;
                          const displayDiscount = isTapesCategory ? (size.discount ?? 0) : (product.discount || 0);
                          
                          // Ensure discount is percentage (0-100), not decimal
                          const discountPercent = displayDiscount > 1 ? displayDiscount / 100 : displayDiscount;
                          
                          // Selling price: MRP - (MRP × Discount%) - discount from MRP only, not from cost
                          const sellingPrice = Math.round(displayPrice - (displayPrice * discountPercent));

                          if (isTapesCategory && !product.hasNoSizeSelection) {
                            // Tapes: Show pricing details in a card format
                            return (
                              <div
                                key={sizeKey}
                                className="p-4 rounded-lg border-2 transition-all bg-white border-teal-200 hover:border-teal-400"
                              >
                                <div className="grid grid-cols-4 gap-2 mb-3">
                                  <div>
                                    <p className="text-xs text-slate-600 font-semibold">Inch Size</p>
                                    <p className="font-bold text-slate-900">{size.name}</p>
                                  </div>
                                  <div>
                                    <p className="text-xs text-slate-600 font-semibold">MRP</p>
                                    <p className="font-semibold text-slate-900">₹{displayPrice.toLocaleString('en-IN')}</p>
                                  </div>
                                  <div>
                                    <p className="text-xs text-slate-600 font-semibold">Discount</p>
                                    <p className="font-semibold text-red-600">{displayDiscount}%</p>
                                  </div>
                                  <div>
                                    <p className="text-xs text-slate-600 font-semibold">Selling Price</p>
                                    <p className="font-bold text-teal-600">₹{sellingPrice.toLocaleString('en-IN')}</p>
                                  </div>
                                </div>
                                
                                <div className="flex justify-between items-center pt-3 border-t border-slate-200">
                                  <div>
                                    <p className="text-xs text-green-600">✓ Available</p>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <button
                                      onClick={() => {
                                        if (qty > 0) {
                                          updateProductSelections(product.id, sizeKey, qty - 1);
                                        }
                                      }}
                                      className="w-8 h-8 bg-slate-200 hover:bg-slate-300 rounded font-bold text-slate-700"
                                    >
                                      −
                                    </button>
                                    <input
                                      type="number"
                                      value={qty}
                                      onChange={(e) => {
                                        const newQty = Math.max(0, parseInt(e.target.value) || 0);
                                        updateProductSelections(product.id, sizeKey, newQty);
                                      }}
                                      className="w-12 text-center px-2 py-1 border border-slate-200 rounded font-semibold"
                                    />
                                    <button
                                      onClick={() => updateProductSelections(product.id, sizeKey, qty + 1)}
                                      className="w-8 h-8 bg-teal-600 hover:bg-teal-700 text-white rounded font-bold"
                                      title="Add"
                                    >
                                      +
                                    </button>
                                  </div>
                                </div>
                              </div>
                            );
                          } else {
                            // Regular categories: Show original list format
                            return (
                              <div
                                key={sizeKey}
                                className="flex justify-between items-center p-3 rounded-lg border-2 transition-all bg-slate-50 border-slate-200 hover:border-teal-300"
                              >
                                <div>
                                  <p className="font-semibold text-slate-900">Size {size.name}</p>
                                  <p className="text-xs text-green-600">✓ Available</p>
                                </div>

                                <div className="flex items-center gap-2">
                                  <button
                                    onClick={() => {
                                      if (qty > 0) {
                                        updateProductSelections(product.id, sizeKey, qty - 1);
                                      }
                                    }}
                                    className="w-8 h-8 bg-slate-200 hover:bg-slate-300 rounded font-bold text-slate-700"
                                  >
                                    −
                                  </button>
                                  <input
                                    type="number"
                                    value={qty}
                                    onChange={(e) => {
                                      const newQty = Math.max(0, parseInt(e.target.value) || 0);
                                      updateProductSelections(product.id, sizeKey, newQty);
                                    }}
                                    className="w-12 text-center px-2 py-1 border border-slate-200 rounded font-semibold"
                                  />
                                  <button
                                    onClick={() => updateProductSelections(product.id, sizeKey, qty + 1)}
                                    className="w-8 h-8 bg-teal-600 hover:bg-teal-700 text-white rounded font-bold"
                                    title="Add"
                                  >
                                    +
                                  </button>
                                </div>
                              </div>
                            );
                          }
                        })}
                      </div>
                    ) : (
                      <p className="text-slate-500 text-center py-4">No sizes available for this product</p>
                    )}
                  </div>

                  {/* Selected Summary */}
                  {hasSelection && (
                    <div className="mb-6 p-4 bg-teal-50 border-2 border-teal-200 rounded-lg overflow-visible">
                      <p className="text-sm font-semibold text-slate-700 mb-3">Selected:</p>
                      <div className="space-y-2 mb-3">
                        {product.hasNoSizeSelection ? (
                          <p className="text-sm text-slate-800 font-medium">
                            • Quantity: <span className="font-bold text-teal-600">{currentSelections['no-size'] || 0} {(currentSelections['no-size'] || 0) === 1 ? 'item' : 'items'}</span>
                          </p>
                        ) : (
                          product.sizes && product.sizes.map((size) => {
                            const sizeKey = `${size.sizeId || size.id}`;
                            const qty = currentSelections[sizeKey] || 0;
                            if (qty > 0) {
                              return (
                                <p key={sizeKey} className="text-sm text-slate-800 font-medium">
                                  • Size {size.name}: <span className="font-bold text-teal-600">{qty} {qty === 1 ? 'item' : 'items'}</span>
                                </p>
                              );
                            }
                            return null;
                          })
                        )}
                      </div>
                      <div className="pt-3 border-t border-teal-200 space-y-2">
                        <div className="flex items-center justify-between gap-2">
                          <span className="font-semibold text-slate-700">Quantity:</span>
                          <span className="text-lg font-bold text-teal-600">{totalQty} {totalQty === 1 ? 'item' : 'items'}</span>
                        </div>
                        <div className="flex items-center justify-between gap-2">
                          <span className="font-semibold text-slate-700">Total Amount:</span>
                          <span className="text-lg font-bold text-teal-600">₹{totalAmount.toLocaleString('en-IN')}</span>
                        </div>
                        {modalOfferApplied && (
                          <div className="text-xs font-semibold text-green-700 bg-green-50 border border-green-200 px-2 py-1 rounded inline-block">
                            {modalOfferApplied.label || `Extra ${modalOfferApplied.discountPercent}% off applied`}
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="flex gap-3">
                    <button
                      onClick={() => setSizeModalOpen(null)}
                      className="flex-1 px-4 py-2 bg-slate-200 hover:bg-slate-300 text-slate-900 font-semibold rounded-lg transition-colors"
                    >
                      Cancel
                    </button>
                    <motion.button
                      onClick={() => addToCart(products.find(p => p.id === sizeModalOpen))}
                      disabled={totalQty === 0 || cartLoading[sizeModalOpen]}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      className="flex-1 px-4 py-2 bg-teal-600 hover:bg-teal-700 disabled:bg-slate-300 text-white font-semibold rounded-lg transition-colors flex items-center justify-center gap-2"
                    >
                      <ShoppingCart size={16} />
                      {cartLoading[sizeModalOpen] ? 'Adding...' : 'Add to Cart'}
                    </motion.button>
                  </div>
                </div>
              );
            })()}
          </motion.div>
        </motion.div>
      )}
    </Section>
  );
};

const CTASection = () => {
  return (
    <Section className="bg-gradient-to-r from-teal-600 to-blue-600 text-white">
      <Container>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center py-8"
        >
          <h2 className="text-3xl md:text-4xl font-bold mb-4">Need Expert Consultation?</h2>
          <p className="text-lg text-teal-100 mb-8 max-w-2xl mx-auto">
            Our healthcare professionals are available 24/7 to help you choose the right equipment for your needs.
          </p>
          <Link
            to="/contact"
            className="px-8 py-4 text-lg font-semibold rounded-lg bg-white text-teal-600 hover:bg-slate-100 transition-all duration-200 shadow-lg inline-flex items-center gap-2"
          >
            Contact Us Today
            <ChevronRight size={20} />
          </Link>
        </motion.div>
      </Container>
    </Section>
  );
};

export default function Products() {
  const { token } = useAuth();
  const [products, setProducts] = useState(() => {
    // Load from cache immediately
    try {
      const cached = localStorage.getItem('products_cache_v3');
      return cached ? JSON.parse(cached) : [];
    } catch (e) {
      return [];
    }
  });
  const [categories, setCategories] = useState(() => {
    // Load from cache immediately
    try {
      const cached = localStorage.getItem('categories_cache_v3');
      return cached ? JSON.parse(cached) : [];
    } catch (e) {
      return [];
    }
  });
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    // Clear old cache keys so stale data never loads
    try {
      localStorage.removeItem('products_cache_v2');
      localStorage.removeItem('categories_cache_v2');
    } catch (e) {
      // ignore
    }

    // Fetch fresh data in background
    const fetchData = async () => {
      try {
        const [productsRes, categoriesRes] = await Promise.all([
          fetch(`${API_BASE_URL}/api/products`),
          fetch(`${API_BASE_URL}/api/products/categories/all`),
        ]);

        const productsData = await productsRes.json();
        const categoriesData = await categoriesRes.json();

        const prods = productsData.data || [];
        const cats = categoriesData.data || [];

        // Update state
        setProducts(prods);
        setCategories(cats);

        // Cache for next load
        localStorage.setItem('products_cache_v3', JSON.stringify(prods));
        localStorage.setItem('categories_cache_v3', JSON.stringify(cats));
      } catch (error) {
        console.log('Could not fetch data');
      }
    };

    // Fetch in background
    fetchData();
  }, []);

  const handleCategoryChange = (category) => {
    setSelectedCategory(category);
  };

  const handleSearch = (term) => {
    setSearchTerm(term);
  }

  return (
    <div className="w-full bg-white">
      <HeroSection />
      <ProductGridSection
        products={products}
        categories={categories}
        onCategoryChange={handleCategoryChange}
        selectedCategory={selectedCategory}
        onSearch={handleSearch}
        searchTerm={searchTerm}
        token={token}
        isLoading={products.length === 0}
      />
      <CTASection />
    </div>
  );
}
